// Flavor isolation for the shared Plannera-Global ("general") database.
//
// car/room/spa/nail/yacht all live in ONE Parse DB (one User + one Business table),
// so without this a car user could log into spa.plannera.app. We lock every user
// (admins AND coworkers) to the flavor named in their `registered_using`: on any OTHER
// general flavor they're blocked with "registered for {Flavor}". One email/phone = one
// flavor — two flavors need two accounts.
//
// ONLY enforced on general builds (PROJECT=general). The DreamDiner 'planner' backend
// (table / tablePL) is a separate DB and is never gated.

import { GENERAL_FLAVORS } from 'src/flavors'
import { FLAVOR_CLONE_OF } from 'src/flavors/settings'
import type { BusinessType } from 'src/flavors/types'

export const isGeneralBuild = process.env.PROJECT === 'general'
export const currentFlavor = process.env.BUSINESS_TYPE || 'table'

/**
 * The flavor a user BELONGS to, which is not always the flavor of the build.
 *
 * A clone (wellza → spa) is a second app over the SAME vertical and the same data: the
 * spa demo account, and every real spa customer, must be able to sign into it. Locking
 * on the raw build flavor told them "This account is registered for Spa. Please use the
 * Spa app" — in an app that IS the spa app under another name.
 *
 * Both sides of the comparison are resolved, so it works in either direction: a user
 * who registers inside wellza (`planner_web_wellza`) can also sign into spa.
 */
export function identityFlavor(flavor: string | null): string | null {
  if (!flavor) return flavor
  return FLAVOR_CLONE_OF[flavor as BusinessType] ?? flavor
}

/**
 * Every flavor of the same TYPE: the base plus all its clones, whichever one you start
 * from — spa → ['spa', 'wellza'], wellza → ['spa', 'wellza'], car → ['car'] until car
 * gets clones (then ['car', 'cargo', 'carplan', …] with no change here).
 *
 * ⭐ Owner, 2026-09-27: a type is ONE pool of businesses and each flavor is just another
 * storefront onto it. A business belongs to the type, whichever app created it, so every
 * PUBLIC listing (marketplace, search, Popular places) must query the whole family, never
 * `equalTo('businessType', <build flavor>)` — that showed a wellza guest zero spas, and a
 * spa guest nothing made in the wellza app.
 */
export function flavorFamily(flavor: string): string[] {
  const base = identityFlavor(flavor) || flavor
  const clones = Object.entries(FLAVOR_CLONE_OF as Record<string, string>)
    .filter(([, b]) => b === base)
    .map(([clone]) => clone)
  return [base, ...clones]
}

/**
 * The general flavor a `registered_using` string belongs to, or null if it names none
 * (legacy 'planner' / 'planner_web' / old 'planner_web_coworker'). Matched by exact
 * `_`-delimited TOKEN (not substring), so a flavor name can never collide with a longer
 * one (e.g. 'hair' vs 'hairdresser'). Clean shapes (planner_web_car, planner_ios_car,
 * planner_web_room_coworker) resolve; a malformed 'userCar' token does NOT (→ untagged,
 * re-claimed on next register/coworker-add).
 */
export function flavorOf(registeredUsing: string | undefined | null): string | null {
  const tokens = (registeredUsing || '').toLowerCase().split('_')
  return GENERAL_FLAVORS.find((f) => tokens.includes(f)) ?? null
}

/** Title-case flavor for user-facing messages (car → Car). */
export function flavorDisplayName(flavor: string | null | undefined): string {
  return flavor ? flavor.charAt(0).toUpperCase() + flavor.slice(1) : ''
}

/**
 * On a general build, returns a block when `registered_using` does NOT match THIS flavor
 * (different general flavor OR untagged). Returns null = allowed. Always null on non-general
 * builds. `otherFlavor` is the flavor they belong to (or null if untagged).
 */
export function flavorLockBlock(
  registeredUsing: string | undefined | null,
): { otherFlavor: string | null } | null {
  if (!isGeneralBuild) return null
  const f = flavorOf(registeredUsing)
  // Compare IDENTITIES, not keys: a clone and its base share one user pool.
  if (identityFlavor(f) === identityFlavor(currentFlavor)) return null
  return { otherFlavor: f }
}

/** Thrown when a user is blocked by flavor isolation. `otherFlavor` = the flavor they belong to. */
export class FlavorLockError extends Error {
  otherFlavor: string | null
  constructor(otherFlavor: string | null) {
    super(`flavor-locked:${otherFlavor ?? 'none'}`)
    this.name = 'FlavorLockError'
    this.otherFlavor = otherFlavor
  }
}
